import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { DisputesTable } from "@/components/admin/DisputesTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

interface DisputeData {
  id: string;
  orderId: string;
  status: string;
  reason: string;
  customerNote?: string;
  openedAt: string;
  hoursOpen: number;
  meta?: any;
  order: {
    publicId: string;
    status: string;
    customer: {
      publicId: string;
      name: string;
      phone?: string | null;
    };
  };
}

export default function AdminDisputesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [disputes, setDisputes] = useState<DisputeData[]>([]);
  const [loading, setLoading] = useState(true);

  const status = searchParams.get("status") || "all";
  const requestedResolution = searchParams.get("requestedResolution") || "all";
  const q = searchParams.get("q") || "";

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    // Check user role - would need to get from user metadata or separate call
    // For now, assuming the user object has role info
    fetchDisputes();
  }, [user, authLoading, searchParams]);

  async function fetchDisputes() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (requestedResolution && requestedResolution !== "all") {
        params.set("requestedResolution", requestedResolution);
      }
      if (q) params.set("q", q);

      const response = await fetch(`/api/admin/disputes?${params}`);

      if (!response.ok) {
        console.error("Failed to fetch disputes");
        setDisputes([]);
        return;
      }

      const data = await response.json();
      setDisputes(data.disputes || []);
    } catch (error) {
      console.error("Error fetching disputes:", error);
      setDisputes([]);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newQ = formData.get("q") as string;
    const newParams = new URLSearchParams(searchParams);
    if (newQ) {
      newParams.set("q", newQ);
    } else {
      newParams.delete("q");
    }
    setSearchParams(newParams);
  }

  function handleStatusChange(value: string) {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      newParams.set("status", value);
    } else {
      newParams.delete("status");
    }
    setSearchParams(newParams);
  }

  function handleResolutionChange(value: string) {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      newParams.set("requestedResolution", value);
    } else {
      newParams.delete("requestedResolution");
    }
    setSearchParams(newParams);
  }

  if (authLoading || loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <PageHeader title="Disputes" subtitle="Manage customer disputes and returns" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader title="Disputes" subtitle="Manage customer disputes and returns" />

      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <form onSubmit={handleSearch}>
            <Input
              name="q"
              placeholder="Search by order ID, customer name, phone..."
              defaultValue={q}
              className="max-w-md"
            />
          </form>
        </div>

        <div className="flex gap-2">
          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="OPENED">Opened</SelectItem>
              <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
              <SelectItem value="CUSTOMER_ACTION_REQUIRED">
                Action Required
              </SelectItem>
              <SelectItem value="CLOSED">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={requestedResolution} onValueChange={handleResolutionChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="DISCOUNT">Discount</SelectItem>
              <SelectItem value="REVISE_QUOTE">Revise Quote</SelectItem>
              <SelectItem value="RETURN">Return</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DisputesTable disputes={disputes} />
    </div>
  );
}
